<template>
  <div :class="bem()">
    <div :class="bem('intro')">
      <BaseHeading
        size="h2"
        tag="h3"
        class="t-gradient"
      >
        Own a part of our Future.
      </BaseHeading>

      <BodyCopy>
        Once again we're inviting you, our community, to join us. Register your interest below to find out more and receive priority access to our campaign, ahead of the crowd.
      </BodyCopy>
    </div>

    <SlideDownTransition>
      <div
        v-if="!showSuccessMessage"
        :class="bem('form')"
      >
        <div :class="bem('input-group', { inline: true })">
          <!-- First name -->
          <TextInput
            v-model="state.firstName"
            type="text"
            placeholder="First Name"
            mode="grey"
            border="blue"
            :has-error="v$.firstName.$errors.length > 0"
            :disabled="loading || submitted"
            :class="bem('input', { grow: true })"
            @blur="handleBlur('firstName')"
            @focus="handleFocus('firstName')"
          />

          <!-- Last name -->
          <TextInput
            v-model="state.lastName"
            type="text"
            placeholder="Last Name"
            mode="grey"
            border="blue"
            :has-error="v$.lastName.$errors.length > 0"
            :disabled="loading || submitted"
            :class="bem('input', { grow: true })"
            @blur="handleBlur('lastName')"
            @focus="handleFocus('lastName')"
          />
        </div>

        <!-- Email -->
        <TextInput
          v-model="state.email"
          type="email"
          placeholder="your@email.com"
          mode="grey"
          border="blue"
          :has-error="v$.email.$errors.length > 0"
          :disabled="loading || submitted"
          :class="bem('input')"
          @blur="handleBlur('email')"
          @focus="handleFocus('email')"
        />

        <!-- Interest Level -->
        <InvestLevelSelect
          v-model="state.interestLevel"
          :disabled="loading || submitted"
          :class="bem('input')"
        />

        <!-- Submit button -->
        <div :class="bem('input-group', { submit: true })">
          <PrimaryButton
            :class="bem('submit-button')"
            :is-loading="loading"
            :is-complete="submitted"
            :disabled="submitDisabled"
            preload-icons
            hide-slot-whilst-loading-or-complete
            grow
            @click="submit"
          >
            Register Your Interest
          </PrimaryButton>

          <SlideDownTransition>
            <BodyCopy
              v-if="errorMessage"
              :class="bem('error-message')"
            >
              {{ errorMessage }}
            </BodyCopy>
          </SlideDownTransition>

          <BodyCopy
            small
            :class="bem('disclaimer')"
          >
            By submitting your details you agree to SunGod's
            <PrimaryLineLink
              size="inherit"
              is-button
              @click.stop="openPrivacyPolicy"
            >
              privacy policy.
            </PrimaryLineLink>
            Your pledge amount is purely an indication of interest and does not constitute a financial obligation or commitment.
          </BodyCopy>
        </div>
      </div>
    </SlideDownTransition>
    
    <SlideDownTransition>
      <div
        v-if="showSuccessMessage"
        :class="bem('completion-message')"
      >
        <VisionIcon
          icon="checkmark"
          colour="white"
          size="x-small"
        />

        <BodyCopy>
          Thanks for registering your interest, we'll be in touch with more information, updates and next steps!
        </BodyCopy>
      </div>
    </SlideDownTransition>

    <div :class="bem('footer')">
      <BodyCopy>
        Investments of this nature carry risks to your capital. Please Invest Aware.
      </BodyCopy>

      <BodyCopy>
        Unanswered Question?
        <SecondaryLineLink
          size="inherit"
          is-button
          @click.stop="viewFAQs"
        >
          View the FAQs
        </SecondaryLineLink>.
      </BodyCopy>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { debounce } from '~/assets/js/helpers'
import { required, email, helpers } from '@vuelidate/validators'

export default {
  name: 'InvestPreregForm',
  setup () {
    const bem = useBEM('InvestPreregForm')

    const state = reactive({
      firstName: '',
      lastName: '',
      email: '',
      interestLevel: ''
    })

    const rules = {
      firstName: {
        required: helpers.withMessage('Please enter your first name.', required)
      },
      lastName: {
        required: helpers.withMessage('Please enter your last name.', required)
      },
      email: {
        required: helpers.withMessage('Please enter your email address.', required),
        email: helpers.withMessage('Please enter a valid email address.', email)
      },
      interestLevel: {
        required: helpers.withMessage('Please select your interest level.', required)
      }
    }

    const v$ = useVuelidate(rules, state)
    return { bem, state, v$ }
  },
  data: () => ({
    focussed: null,
    loading: false,
    submitted: false,
    showSuccessMessage: false,
    apiErrorMessage: null
  }),
  computed: {
    errorMessage () {
      if (this.apiErrorMessage) return this.apiErrorMessage
      return this.v$.$errors.length ? this.v$.$errors[0].$message : null
    },
    submitDisabled () {
      // if (this.$v.$anyError || this.$v.$invalid) return true
      if (Object.values(this.state).some(value => !value)) return true
      if (this.loading) return true
      if (this.submitted) return true

      return false
    },
    cleanedLevel () {
      return (this.state.interestLevel || '').replace(/£/g, '').replace(/ /g, '').replace(/k/g, '000')
    }
  },
  watch: {
    interestLevel () {
      this.apiErrorMessage = false
      this.focussed = null
    }
  },
  mounted () {
    this.progress = debounce(this.progress, 250)
  },
  methods: {
    async submit () {
      // Validate state
      this.v$.$touch()

      // // If there are errors then return now
      if (this.v$.$anyError || this.v$.$invalid) return

      this.loading = true

      try {
        this.$track.invest.submit(this.state)

        await $fetch(
          '/api/invest/submit/',
          {
            method: 'POST',
            body: {
              email: this.state.email,
              firstName: this.state.firstName,
              lastName: this.state.lastName,
              amount: this.cleanedLevel
            }
          }
        )

        this.submitted = true
        this.showSuccessMessage = true
      } catch (error) {
        this.$sentry.captureException(error)
        console.error(error)
        this.apiErrorMessage = 'Unable to submit your registration. Please try again later or contact investors@sungod.co'
      }

      this.loading = false
    },
    async progress () {
      this.$track.invest.progress(this.state)
    },
    openPrivacyPolicy () {
      this.$store.dispatch('sidebar/OPEN_SIDEBAR_PAGE', { slug: 'privacy-policy' })
    },
    viewFAQs () {
      this.$scrollTo('#row-142784320')
    },
    handleFocus (field) {
      this.focussed = field
    },
    async handleBlur (field) {
      if (this.focussed === field) this.focussed = null

      this.v$[field]?.$touch()
      this.apiErrorMessage = false
      await this.progress()
    },
    async validateEmail (email) {
      if (!email) return true // Don't validate if empty, this will be handled by the required validator

      try {
        const response = await this.$store.dispatch('checkout/CHECK_EMAIL', { email, sendSMS: false })

        if ('valid' in response) return response.valid
      } catch (error) {
        this.$sentry.captureException(error)
        console.error(error)
      }

      return true // Failover to assuming it's valid
    }
  }
}
</script>

<style lang="postcss">
.InvestPreregForm {
  @apply w-full
    flex flex-col
    space-y-md
    mt-md;

  border-radius: 10px;
  --one-column: calc(1 / 12 * (100% - (11 * 32px)) + 32px);

  @screen sm {
    @apply border border-grey-500;
    padding: theme('spacing.lg') var(--one-column);
  }
}

.InvestPreregForm__intro,
.InvestPreregForm__footer {
  @apply flex flex-col
    space-y-xs;
}

.InvestPreregForm__form {
  @apply flex flex-col
    space-y-sm;
}

.InvestPreregForm__input--grow {
  @apply flex-grow;
}

.InvestPreregForm .InvestPreregForm__input .TextInput__input {
  @apply px-sm;
}

.InvestPreregForm__input-group {
  &--inline {
    @apply flex flex-row
      space-x-sm
      w-full;

    @screen smMax {
      @apply flex-col
        space-y-sm
        space-x-0;
    }
  }

  &--submit {
    @apply mt-[30px];
  }
}

.InvestPreregForm__submit-button {
  @apply mb-xs;
}

.InvestPreregForm__disclaimer {
  @apply pt-xs;
}

.InvestPreregForm__error-message {
  @apply text-red;
}

.InvestPreregForm__completion-message {
  @apply flex items-center space-x-xxs
    bg-green text-white
    py-xs px-sm;
  border-radius: 4px;
}
</style>